import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Container, Row, Col, Card, Form, Button, ProgressBar } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { ChargingBASE_URL } from '../../Config';

// Columns for DataTable
const columns = [
  {
    name: "Agent Name",
    selector: (row) => row.agentName,
    sortable: true,
  },
  {
    name: "Company Name",
    selector: (row) => row.companyName,
    sortable: true,
  },
  {
    name: "Timestamp",
    selector: (row) => row.timestamp,
    sortable: true,
  },
  {
    name: "Reason",
    selector: (row) => row.reason,
    sortable: true,
  },
  {
    name: "Amount",
    selector: (row) => row.amount,
    sortable: true,
  },
  {
    name: "Account Number",
    selector: (row) => row.account_Number,
    sortable: true,
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
  },
  {
    name: "Transaction ID",
    selector: (row) => row.transactionId,
    sortable: true,
  },
];

const ReportByNo = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [msisdn, setMsisdn] = useState("");
  const [userId, setUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // State for the search query

  // Get UserId from session storage on component mount
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user) {
      setUserId(user.userId);
    }
  }, []);

  // Fetch data when the user submits the form
  const fetchData = async () => {
    if (!msisdn) {
      setError("MSISDN is required");
      return;
    }

    const url = new URL(`${ChargingBASE_URL}eportByNo`);
    const params = {
      UserId: userId,
      Msisdn: msisdn,
    };
    url.search = new URLSearchParams(params).toString();

    try {
      setIsLoading(true);
      setProgress(1); // Start progress at 1%

      // Simulate the progress bar loader
      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(progressInterval); // Stop interval when progress reaches 100%
            return 100;
          }
          return prevProgress + 5; // Increase progress by 5%
        });
      }, 100);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "*/*",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const result = await response.json();
      setData(result); // Set the fetched data
      setFilteredData(result); // Set the filtered data initially to show all
      setError(null); // Clear error state
    } catch (error) {
      setError(error.message); // Set error message if fetch fails
    } finally {
      setIsLoading(false); // Stop the loading state after fetch is completed
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // Handle search filter input
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query); // Update the search query state

    if (query === "") {
      setFilteredData(data); // Show all data when search query is empty
    } else {
      // Filter data based on search query
      const filtered = data.filter(
        (row) =>
          row.agentName.toLowerCase().includes(query.toLowerCase()) ||
          row.companyName.toLowerCase().includes(query.toLowerCase()) ||
          row.timestamp.toLowerCase().includes(query.toLowerCase()) ||
          row.reason.toLowerCase().includes(query.toLowerCase()) ||
          row.amount.toString().includes(query) ||
          row.account_Number.toString().includes(query) ||
          row.status.toLowerCase().includes(query) ||
          row.transactionId.toString().includes(query)
      );
      setFilteredData(filtered); // Update the filtered data based on search query
    }
  };

  return (
    <>
      {/* Show loading progress bar if data is being fetched */}
      {isLoading && (
        <div className="text-center mt-2">
          <div className="blur-overlay" style={{ position: 'relative' }}>
            <ProgressBar
              animated
              now={progress}
              label={`Loading Data....${progress}%`}
              style={{ width: '100%' }} // Set width to 100%
            />
          </div>
        </div>
      )}

      {/* Error handling */}
      {error && <div className="text-center mt-2">{error}</div>}

      <Row className="justify-content-center">
        <Col lg={10} md={12} sm={12}>
          <Card className="mt-2 shadow-lg">
            <Card.Body>
              {/* Form for MSISDN input */}
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={4} sm={6}>
                    <Form.Group controlId="msisdn">
                      <Form.Control
                        type="text"
                        placeholder="Enter MSISDN"
                        value={msisdn}
                        className="mt-4"
                        onChange={(e) => setMsisdn(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} sm={6}>
                    <Button
                      variant="primary"
                      type="submit"
                      className="mt-4 w-100"
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading..." : "Search"}


                    </Button>
                  </Col>

                  {/* Search Input for filtering results */}

                  <Col md={4} sm={6}>
                    <Form.Control
                      type="text"
                      placeholder="Search......"
                      value={searchQuery}
                      onChange={handleSearch}
                      className="mt-4"
                    />
                  </Col>
                </Row>
              </Form>



              {/* DataTable for displaying the fetched data */}
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                highlightOnHover
                striped
                dense
                paginationPerPage={5}
                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                defaultSortFieldId={1} // Default sort column (Timestamp)
                defaultSortAsc={true} // Default sort order (ascending)
              />

              {/* Download Button appears only after the data is successfully fetched */}
              {filteredData.length > 0 && !isLoading && (
                <CSVLink
                  data={filteredData}
                  filename={"transaction_report.csv"}
                  className="btn btn-success mt-1 w-100"
                >
                  Download CSV
                </CSVLink>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ReportByNo;
